/**
 * Reading a pull request, and working out where a comment may legally go.
 *
 * ⛔ ANCHORING IS ALL-OR-NOTHING. One comment on a line outside the diff fails the entire review
 * call with a 422 and nothing lands — not the other nineteen comments, not the summary. So the set
 * of legal anchors is computed from the pull request's own patches BEFORE anything is posted, and
 * every finding is filtered against it.
 *
 * This module is read-only. Nothing here writes to GitHub.
 */

export interface Anchor {
  readonly path: string
  readonly line: number
  readonly side: "LEFT" | "RIGHT"
}

export interface ChangedFile {
  readonly path: string
  readonly status: string
  readonly additions: number
  readonly deletions: number
  /** Absent for a binary file or when GitHub truncates a very large patch. */
  readonly patch?: string
}

export interface PullRequest {
  readonly number: number
  readonly title: string
  readonly headSha: string
  readonly baseSha: string
  readonly baseRef: string
  readonly draft: boolean
  readonly state: string
  readonly changedFiles: number
}

/** One hunk of a unified diff, in post-image coordinates. */
export interface Hunk {
  readonly oldStart: number
  readonly oldLines: number
  readonly newStart: number
  readonly newLines: number
}

const HUNK_RE = /^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@/

/**
 * Parse a unified patch into hunks.
 *
 * ⚠️ A count omitted in a hunk header means 1, not 0 — `@@ -5 +5 @@` is a single line. Defaulting it
 * to zero silently drops every one-line hunk from the anchor set, and a one-line change is the most
 * common shape there is.
 */
export function parseHunks(patch: string): Hunk[] {
  const out: Hunk[] = []
  for (const line of patch.split("\n")) {
    const m = HUNK_RE.exec(line)
    if (m === null) continue
    out.push({
      oldStart: Number(m[1]),
      oldLines: m[2] === undefined ? 1 : Number(m[2]),
      newStart: Number(m[3]),
      newLines: m[4] === undefined ? 1 : Number(m[4]),
    })
  }
  return out
}

/** A line that a review comment may anchor to, and which side it lives on. */
export interface AnchorSet {
  /** `path` → post-image line numbers a RIGHT-side comment may use. */
  readonly right: ReadonlyMap<string, ReadonlySet<number>>
  /** `path` → pre-image line numbers a LEFT-side comment may use. */
  readonly left: ReadonlyMap<string, ReadonlySet<number>>
  /** Post-image lines this pull request ADDED or CHANGED — the `pre-existing` test. */
  readonly changed: ReadonlyMap<string, ReadonlySet<number>>
  /** Files excluded from the set, with the reason, so a gap is named rather than silent. */
  readonly skipped: ReadonlyArray<{ path: string; reason: string }>
}

/**
 * Build the anchor set from a pull request's files.
 *
 * ⭐ Three distinct sets, and conflating them is the bug this exists to prevent:
 *   - **right** — every line visible in the post-image of a hunk, INCLUDING unchanged context. A
 *     comment may anchor here.
 *   - **left** — deleted and context lines in the pre-image.
 *   - **changed** — only lines this pull request added or modified. A finding citing a line that is
 *     anchorable but NOT changed is `pre-existing`; it can still be commented on, but it is not
 *     something the author introduced.
 */
export function buildAnchorSet(files: readonly ChangedFile[]): AnchorSet {
  const right = new Map<string, Set<number>>()
  const left = new Map<string, Set<number>>()
  const changed = new Map<string, Set<number>>()
  const skipped: Array<{ path: string; reason: string }> = []

  for (const f of files) {
    if (f.patch === undefined) {
      // ⛔ Named, never dropped silently. A binary file or a patch GitHub truncated means we cannot
      // anchor in it at all, and a reviewer should be told rather than quietly under-reviewed.
      skipped.push({
        path: f.path,
        reason: f.status === "removed" ? "file removed" : "no patch (binary, or truncated by GitHub)",
      })
      continue
    }
    const r = new Set<number>()
    const l = new Set<number>()
    const c = new Set<number>()

    let newLine = 0
    let oldLine = 0
    for (const line of f.patch.split("\n")) {
      const m = HUNK_RE.exec(line)
      if (m !== null) {
        oldLine = Number(m[1])
        newLine = Number(m[3])
        continue
      }
      if (newLine === 0 && oldLine === 0) continue // preamble before the first hunk
      const kind = line[0]
      if (kind === "+") {
        r.add(newLine)
        c.add(newLine)
        newLine++
      } else if (kind === "-") {
        l.add(oldLine)
        oldLine++
      } else if (kind === " ") {
        // Context is anchorable but NOT changed — this distinction is the pre-existing test.
        r.add(newLine)
        l.add(oldLine)
        newLine++
        oldLine++
      }
      // "\\ No newline at end of file" advances neither counter.
    }
    right.set(f.path, r)
    left.set(f.path, l)
    changed.set(f.path, c)
  }
  return { right, left, changed, skipped }
}

/** Whether a comment at this anchor would be accepted by GitHub. */
export function isAnchorable(set: AnchorSet, a: Anchor): boolean {
  const m = a.side === "RIGHT" ? set.right : set.left
  return m.get(a.path)?.has(a.line) ?? false
}

/**
 * Whether this pull request introduced the line.
 *
 * ⛔ Computed from the hunks, never judged. False ⇒ the finding is `pre-existing`, whatever it says
 * about itself.
 */
export function wasIntroducedHere(set: AnchorSet, a: Anchor): boolean {
  if (a.side !== "RIGHT") return false
  return set.changed.get(a.path)?.has(a.line) ?? false
}

/**
 * Split findings into those that can be posted at their own anchor and those that cannot.
 *
 * ⛔ Call this before building a review. One unanchorable comment rejects the whole call.
 */
export function partitionByAnchor<T extends { anchor: Anchor }>(
  set: AnchorSet,
  findings: readonly T[],
): { anchored: T[]; unanchorable: T[] } {
  const anchored: T[] = []
  const unanchorable: T[] = []
  for (const f of findings) (isAnchorable(set, f.anchor) ? anchored : unanchorable).push(f)
  return { anchored, unanchorable }
}

// ── The API ────────────────────────────────────────────────────────────────────────────────────

export interface GitHubOptions {
  readonly token: string
  readonly baseUrl?: string
  readonly fetchImpl?: typeof fetch
}

export class GitHubError extends Error {
  constructor(
    readonly status: number,
    readonly body: string,
  ) {
    super(`GitHub ${status}: ${body.slice(0, 200)}`)
    this.name = "GitHubError"
  }
}

export class GitHub {
  private readonly base: string
  private readonly f: typeof fetch

  constructor(private readonly opts: GitHubOptions) {
    this.base = opts.baseUrl ?? "https://api.github.com"
    this.f = opts.fetchImpl ?? fetch
  }

  private async get<T>(path: string): Promise<T> {
    const res = await this.f(`${this.base}${path}`, {
      headers: {
        authorization: `Bearer ${this.opts.token}`,
        accept: "application/vnd.github+json",
        "user-agent": "sibyl",
      },
    })
    if (!res.ok) throw new GitHubError(res.status, await res.text())
    return (await res.json()) as T
  }

  async pullRequest(repo: string, number: number): Promise<PullRequest> {
    const d = await this.get<Record<string, any>>(`/repos/${repo}/pulls/${number}`)
    return {
      number: d["number"],
      title: d["title"],
      headSha: d["head"]["sha"],
      baseSha: d["base"]["sha"],
      baseRef: d["base"]["ref"],
      draft: d["draft"] === true,
      state: d["state"],
      changedFiles: d["changed_files"] ?? 0,
    }
  }

  /**
   * Every changed file, paginated to exhaustion.
   *
   * ⚠️ A partial file list produces an anchor set that silently excludes real lines, so a finding in
   * an unfetched file looks unanchorable and gets degraded for no reason. Pagination is walked to
   * the end rather than taking the first page.
   */
  async changedFiles(repo: string, number: number): Promise<ChangedFile[]> {
    const out: ChangedFile[] = []
    for (let page = 1; ; page++) {
      const batch = await this.get<Array<Record<string, any>>>(
        `/repos/${repo}/pulls/${number}/files?per_page=100&page=${page}`,
      )
      for (const f of batch) {
        out.push({
          path: f["filename"],
          status: f["status"],
          additions: f["additions"] ?? 0,
          deletions: f["deletions"] ?? 0,
          ...(typeof f["patch"] === "string" ? { patch: f["patch"] as string } : {}),
        })
      }
      if (batch.length < 100) return out
    }
  }
}
